import { useEffect, useState, useRef } from 'react';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../../constants/ActionTypes';
import { useDispatch } from 'react-redux';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';

const useForm = () => {
  const dispatch = useDispatch();
  const isMounted = useRef(true);

  const [siteAssessments, setSiteAssessments] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);

  const [emailList, setEmailList] = useState([]);
  const [checkedEmails, setCheckedEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  const [smsList, setSmsList] = useState([]);
  const [checkedSMS, setCheckedSMS] = useState([]);
  const [smsInput, setSmsInput] = useState('');
  const [smsError, setSmsError] = useState('');

  const [allUsers, setAllUsers] = useState([]);
  const [inAppUsers, setInAppUsers] = useState([]);
  const [checkedInAppUsers, setCheckedInAppUsers] = useState([]);
  const [inAppInput, setInAppInput] = useState('');

  const [webAppUsers, setWebAppUsers] = useState([]);
  const [checkedWebAppUsers, setCheckedWebAppUsers] = useState([]);
  const [webAppInput, setWebAppInput] = useState('');

  const [whNumberList, setWhNumberList] = useState([]);
  const [selectedWH, setSelectedWH] = useState(null);

  useEffect(() => {
    const fetchContractData = async () => {
      try {
        const response = await customAxios.get('/Ops/GetContracts', {
          headers: authHeader(),
        });

        console.log('Contracts response:', response.data);

        // Use Promise.all to resolve all async map calls
        const contractIDs = await Promise.all(
          response.data.map(async contract => {
            const site = await customAxios.get(`/CRM/GetCRMSiteDetails?siteAssessmentId=${contract.siteAssessmentID}`, {
              headers: authHeader(),
            });

            const siteRef = site?.data?.[0]?.siteDetailsRef || '';
            const label = siteRef.toUpperCase().startsWith('WH') ? siteRef : `${contract.contractUniqueID} - ${siteRef}`;

            return {
              label,
              value: contract.contractUniqueID,
            };
          }),
        );

        // Optional: Filter out any null/undefined entries after resolving
        const filteredContracts = contractIDs.filter(Boolean);

        console.log('CONTRACT IDS =====', filteredContracts);
        setWhNumberList(filteredContracts);
      } catch (error) {
        console.error('Error fetching contracts:', error);
      }
    };

    fetchContractData();
  }, []);

  useEffect(() => {
    if (!selectedWH) return;

    const fetchDetails = async () => {
      try {
        const response = await customAxios.get('/Ops/GetContracts', {
          headers: authHeader(),
          params: {
            crmID: 0,
            contractUniqueID: selectedWH,
            wH_NumberName: '',
          },
        });

        const contractData = response.data?.[0];
        if (!contractData) return;

        // Extract and clean phone numbers
        const smsNumbers = [contractData.contact1, contractData.contact2, contractData.contact3].filter(Boolean); // removes null, undefined, empty

        setSmsList(smsNumbers);
        setCheckedSMS(smsNumbers);

        // Extract and clean emails
        const emails = [
          contractData.notifyOperatorEmail1,
          contractData.notifyOperatorEmail2,
          contractData.notifyOperatorEmail3,
          contractData.harvestingEmailsForSiteDetails,
          contractData.emailForLogwatchAccess,
        ].filter(Boolean);

        setEmailList(emails);
        setCheckedEmails(emails);
      } catch (error) {
        console.error('Error fetching contract details:', error);
        setSmsList([]);
        setCheckedSMS([]);
        setEmailList([]);
        setCheckedEmails([]);
      }
    };

    fetchDetails();
  }, [selectedWH]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await customAxios.get('/User/GetAllUsers', {
          headers: authHeader(),
        });
        const users = response.data || [];
        setAllUsers(users);
      } catch (error) {
        console.error('Error fetching users:', error);
        setAllUsers([]);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const getSiteAssessmentData = async () => {
      dispatch({ type: FETCH_START });
      try {
        const response = await customAxios.get('/CRM/GetCRMSiteDetails', {
          headers: authHeader(),
        });

        if (isMounted.current) {
          const cleaned = response.data.map(item => ({
            id: item.siteAssessmentID,
            siteAssessmentID: item.siteAssessmentID,
            wH_Number: item.wH_Number,
            CRM_ID: item.CRM_ID,
            title: `${item.siteAssessmentID} | ${item.wH_Number}`,
          }));
          setSiteAssessments(cleaned);
          dispatch({ type: FETCH_SUCCESS });
        }
      } catch (error) {
        if (isMounted.current) {
          setSiteAssessments([]);
          dispatch({ type: FETCH_ERROR, payload: 'Failed to fetch site assessments' });
        }
      }
    };

    getSiteAssessmentData();

    return () => {
      isMounted.current = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!selectedSite?.siteAssessmentID) {
      setEmailList([]);
      setCheckedEmails([]);
      setSmsList([]);
      setCheckedSMS([]);
      return;
    }

    const getNotificationData = async () => {
      try {
        const siteAssessmentID = selectedSite.siteAssessmentID;

        const params = new URLSearchParams();
        params.append('siteAssessmentID', siteAssessmentID.toString());

        const url = `/GenTasks/GetSiteContacts?${params.toString()}`;

        const response = await customAxios.get(url, {
          headers: authHeader(),
        });

        const data = response.data?.[0] || {};

        const emails = [data.operatorEmail1, data.operatorEmail2, data.operatorEmail3].filter(Boolean);

        const dummySMS = [
          `+35383123456${siteAssessmentID}`,
          `+35385123456${siteAssessmentID}`,
          `+35387123456${siteAssessmentID}`,
        ];

        setEmailList(emails);
        setCheckedEmails(emails);
        setSmsList(dummySMS);
        setCheckedSMS(dummySMS);
      } catch (error) {
        console.error('Error fetching site contacts:', error);
        setEmailList([]);
        setCheckedEmails([]);
        setSmsList([]);
        setCheckedSMS([]);
      }
    };

    getNotificationData();
  }, [selectedSite]);

  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidIrishMobile = number => {
    const irishMobileRegex = /^\+3538[3-9]\d{7}$/;
    return irishMobileRegex.test(number);
  };

  const handleEmailInput = e => {
    setEmailInput(e.target.value);
    if (emailError) setEmailError('');
  };

  const handleSMSInput = e => {
    setSmsInput(e.target.value);
    if (smsError) setSmsError('');
  };

  const addEmail = () => {
    if (!emailInput.trim()) {
      setEmailError('Email cannot be empty');
      return;
    }
    if (!isValidEmail(emailInput)) {
      setEmailError('Invalid email format');
      return;
    }
    if (emailList.includes(emailInput)) {
      setEmailError('Email already added');
      return;
    }

    setEmailList(prev => [...prev, emailInput]);
    setCheckedEmails(prev => [...prev, emailInput]);
    setEmailInput('');
    setEmailError('');
  };

  const addSMS = () => {
    if (!smsInput.trim()) {
      setSmsError('Phone number cannot be empty');
      return;
    }
    if (!isValidIrishMobile(smsInput)) {
      setSmsError('Invalid Irish mobile number (e.g. +353831234567)');
      return;
    }
    if (smsList.includes(smsInput)) {
      setSmsError('Phone number already added');
      return;
    }

    setSmsList(prev => [...prev, smsInput]);
    setCheckedSMS(prev => [...prev, smsInput]);
    setSmsInput('');
    setSmsError('');
  };

  const handleEmailCheck = email => {
    setCheckedEmails(prev => (prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]));
  };

  const handleSMSCheck = sms => {
    setCheckedSMS(prev => (prev.includes(sms) ? prev.filter(s => s !== sms) : [...prev, sms]));
  };

  const handleInAppInput = e => setInAppInput(e.target.value);
  const handleWebappInput = e => setWebAppInput(e.target.value);

  const addInAppUser = () => {
    if (!inAppInput.trim() || inAppUsers.includes(inAppInput)) return;
    setInAppUsers(prev => [...prev, inAppInput]);
    setCheckedInAppUsers(prev => [...prev, inAppInput]);
    setInAppInput('');
  };

  const addWebAppUser = () => {
    if (!webAppInput.trim() || webAppUsers.includes(webAppInput)) return;
    setWebAppUsers(prev => [...prev, webAppInput]);
    setCheckedWebAppUsers(prev => [...prev, webAppInput]);
    setWebAppInput('');
  };

  const handleInAppCheck = user =>
    setCheckedInAppUsers(prev => (prev.includes(user) ? prev.filter(u => u !== user) : [...prev, user]));

  const handleWebAppCheck = user =>
    setCheckedWebAppUsers(prev => (prev.includes(user) ? prev.filter(u => u !== user) : [...prev, user]));

  const resetForm = () => {
    setSelectedSite(null);
    setEmailList([]);
    setCheckedEmails([]);
    setEmailInput('');
    setEmailError('');
    setSmsList([]);
    setCheckedSMS([]);
    setSmsInput('');
    setSmsError('');
  };

  return {
    siteAssessments,
    selectedSite,
    setSelectedSite,
    emailList,
    smsList,
    checkedEmails,
    checkedSMS,
    emailInput,
    smsInput,
    emailError,
    smsError,
    handleEmailInput,
    handleSMSInput,
    addEmail,
    addSMS,
    handleEmailCheck,
    handleSMSCheck,
    resetForm,
    allUsers,
    inAppUsers,
    webAppUsers,
    inAppInput,
    webAppInput,
    checkedInAppUsers,
    checkedWebAppUsers,
    handleInAppInput,
    handleWebappInput,
    addInAppUser,
    addWebAppUser,
    handleInAppCheck,
    handleWebAppCheck,
    whNumberList,
    selectedWH,
    setSelectedWH,
  };
};

export default useForm;
